/* eslint @typescript-eslint/no-explicit-any: 0 */
import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { UniqueSelectionDispatcher } from '@angular/cdk/collections';
import {
    AfterContentInit,
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChildren,
    Directive,
    ElementRef,
    EventEmitter,
    forwardRef,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    QueryList,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { tap } from 'rxjs/operators';

// Increasing integer for generating unique ids for checkbox components.
let nextUniqueId = 0;
export const MAT_RADIO_GROUP_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => RadioGroupDirective),
    multi: true,
};

export class RadioChange {
    constructor(
        /** The MatRadioButton that emits the change event. */
        public source: InputRadioComponent,
        /** The value of the MatRadioButton. */
        public value: any,
    ) {}
}

@Directive({
    selector: '[caGroup]',
    exportAs: 'RadioGroupDirective',
    providers: [MAT_RADIO_GROUP_CONTROL_VALUE_ACCESSOR],
    host: {
        role: 'radiogroup',
        class: 'caGroup',
    },
})
export class RadioGroupDirective implements AfterContentInit, ControlValueAccessor {
    /** Theme color for all of the radio buttons in the group. */
    /** Name of the radio button group. All radio buttons inside this group will use this name. */
    @Input() get name(): string {
        return this._name;
    }
    set name(value: string) {
        this._name = value;
        this._updateRadioButtonNames();
    }

    /** Whether the labels should appear after or before the radio-buttons. Defaults to 'after' */
    @Input() get labelPosition(): 'before' | 'after' {
        return this._labelPosition;
    }
    set labelPosition(v) {
        this._labelPosition = v === 'before' ? 'before' : 'after';
        this._markRadiosForCheck();
    }

    /**
     * Value for the radio-group. Should equal the value of the selected radio button if there is
     * a corresponding radio button with a matching value. If there is not such a corresponding
     * radio button, this value persists to be applied in case a new radio button is added with a
     * matching value.
     */
    @Input() get value(): any {
        return this._value;
    }
    set value(newValue: any) {
        if (this._value !== newValue) {
            // Set this before proceeding to ensure no circular loop occurs with selection.
            this._value = newValue;

            this._updateSelectedRadioFromValue();
            this._checkSelectedRadioButton();
        }
    }

    /**
     * The currently selected radio button. If set to a new radio button, the radio group value
     * will be updated to match the new selected button.
     */
    @Input() get selected(): InputRadioComponent | null {
        return this._selected;
    }
    set selected(selected: InputRadioComponent | null) {
        this._selected = selected;
        this.value = selected ? selected.value : null;
        this._checkSelectedRadioButton();
    }

    /** Whether the radio group is disabled */
    @Input() get disabled(): boolean {
        return this._disabled;
    }
    set disabled(value) {
        this._disabled = coerceBooleanProperty(value);
        this._markRadiosForCheck();
    }

    /** Whether the radio group is required */
    @Input() get required(): boolean {
        return this._required;
    }
    set required(value: boolean) {
        this._required = coerceBooleanProperty(value);
        this._markRadiosForCheck();
    }

    /**
     * Event emitted when the group value changes.
     * Change events are only emitted when the value changes due to user interaction with
     * a radio button (the same behavior as `<input type-"radio">`).
     */
    @Output() readonly change: EventEmitter<RadioChange> = new EventEmitter<RadioChange>();

    /** Child radio buttons. */
    @ContentChildren(forwardRef(() => InputRadioComponent), { descendants: true }) _radios: QueryList<InputRadioComponent>;
    /** Selected value for the radio group. */
    private _value: any = null;

    /** The HTML name attribute applied to radio buttons in this group. */
    private _name: string = `ca-radio-group-${nextUniqueId++}`;

    /** The currently selected radio button. Should match value. */
    private _selected: InputRadioComponent | null = null;

    /** Whether the `value` has been set to its initial value. */
    private _isInitialized: boolean = false;

    /** Whether the labels should appear after or before the radio-buttons. Defaults to 'after' */
    private _labelPosition: 'before' | 'after' = 'after';

    /** Whether the radio group is disabled. */
    private _disabled: boolean = false;

    /** Whether the radio group is required. */
    private _required: boolean = false;
    constructor(private _changeDetector: ChangeDetectorRef) {}

    /** The method to be called in order to update ngModel */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _controlValueAccessorChangeFn: (value: any) => void = () => {
        // do nothing
    };

    /**
     * onTouch function registered via registerOnTouch (ControlValueAccessor).
     * @docs-private
     */
    onTouched: () => any = () => {
        // do nothing
    };

    _checkSelectedRadioButton(): void {
        if (this._selected && !this._selected.checked) {
            this._selected.checked = true;
        }
    }

    /**
     * Initialize properties once content children are available.
     * This allows us to propagate relevant attributes to associated buttons.
     */
    ngAfterContentInit(): void {
        // Mark this component as initialized in AfterContentInit because the initial value can
        // possibly be set by NgModel on RadioGroupDirective, and it is possible that the OnInit of the
        // NgModel occurs *after* the OnInit of the RadioGroupDirective.
        this._isInitialized = true;
    }

    /**
     * Mark this group as being "touched" (for ngModel). Meant to be called by the contained
     * radio buttons upon their blur.
     */
    _touch(): void {
        if (this.onTouched) {
            this.onTouched();
        }
    }
    /** Dispatch change event with current selection and group value. */
    _emitChangeEvent(): void {
        if (this._isInitialized) {
            this.change.emit(new RadioChange(this._selected!, this._value));
        }
    }

    _markRadiosForCheck(): void {
        if (this._radios) {
            this._radios.forEach(radio => radio._markForCheck());
        }
    }

    /**
     * Sets the model value. Implemented as part of ControlValueAccessor.
     */
    writeValue(value: any): void {
        this.value = value;
        this._changeDetector.markForCheck();
    }

    /**
     * Registers a callback to be triggered when the model value changes.
     * Implemented as part of ControlValueAccessor.
     * @param fn Callback to be registered.
     */
    registerOnChange(fn: (value: any) => void): void {
        this._controlValueAccessorChangeFn = fn;
    }

    /**
     * Registers a callback to be triggered when the control is touched.
     * Implemented as part of ControlValueAccessor.
     * @param fn Callback to be registered.
     */
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        this._changeDetector.markForCheck();
    }
    private _updateRadioButtonNames(): void {
        if (this._radios) {
            this._radios.forEach(radio => {
                radio.name = this.name;
                radio._markForCheck();
            });
        }
    }

    /** Updates the `selected` radio button from the internal _value state. */
    private _updateSelectedRadioFromValue(): void {
        // If the value already matches the selected radio, do nothing.
        const isAlreadySelected = this._selected !== null && this._selected.value === this._value;

        if (this._radios && !isAlreadySelected) {
            this._selected = null;
            this._radios.forEach(radio => {
                radio.checked = this.value === radio.value;
                if (radio.checked) {
                    this._selected = radio;
                }
            });
        }
    }
}
@Component({
    selector: 'ca-input-radio',
    templateUrl: './input-radio.component.html',
    styleUrls: ['./input-radio.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InputRadioComponent),
            multi: true,
        },
    ],
    inputs: ['tabIndex'],
    encapsulation: ViewEncapsulation.None,
    exportAs: 'caInputRadio',
    host: {
        class: 'ca-input-radio',
        // Needs to be -1 so the `focus` event still fires.
        '[attr.tabindex]': '-1',
        '[attr.aria-label]': 'null',
        '[attr.aria-labelledby]': 'null',
        '[attr.aria-describedby]': 'null',
    },
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputRadioComponent implements OnInit, AfterViewInit, OnDestroy {
    /** Whether this radio button is checked. */
    @Input() get checked(): boolean {
        return this._checked;
    }

    set checked(value: boolean) {
        const newCheckedState = coerceBooleanProperty(value);
        if (this._checked !== newCheckedState) {
            this._checked = newCheckedState;
            if (newCheckedState && this.radioGroup && this.radioGroup.value !== this.value) {
                this.radioGroup.selected = this;
            } else if (!newCheckedState && this.radioGroup && this.radioGroup.value === this.value) {
                // When unchecking the selected radio button, update the selected radio
                // property on the group.
                this.radioGroup.selected = null;
            }

            if (newCheckedState) {
                // Notify all radio buttons with the same name to un-check.
                this._radioDispatcher.notify(this.id, this.name);
            }
            this._changeDetector.markForCheck();
        }
    }

    /** The value of this radio button. */
    @Input() get value(): any {
        return this._value;
    }
    set value(value: any) {
        if (this._value !== value) {
            this._value = value;
            if (this.radioGroup !== null) {
                if (!this.checked) {
                    // Update checked when the value changed to match the radio group's value
                    this.checked = this.radioGroup.value === value;
                }
                if (this.checked) {
                    this.radioGroup.selected = this;
                }
            }
        }
    }

    /** Whether the label should appear after or before the radio button. Defaults to 'after' */
    @Input() get labelPosition(): 'before' | 'after' {
        return this._labelPosition || (this.radioGroup && this.radioGroup.labelPosition) || 'after';
    }
    set labelPosition(value) {
        this._labelPosition = value;
    }

    /** Whether the radio button is disabled. */
    @Input() get disabled(): boolean {
        return this._disabled || (this.radioGroup !== null && this.radioGroup.disabled);
    }
    set disabled(value: boolean) {
        const newDisabledState = coerceBooleanProperty(value);
        if (this._disabled !== newDisabledState) {
            this._disabled = newDisabledState;
            this._changeDetector.markForCheck();
        }
    }

    /** Whether the radio button is required. */
    @Input() get required(): boolean {
        return this._required || (this.radioGroup && this.radioGroup.required);
    }
    set required(value: boolean) {
        this._required = coerceBooleanProperty(value);
    }

    /** ID of the native input element inside `<ca-radio-button>` */
    get inputId(): string {
        return `${this.id || this._uniqueId}-input`;
    }

    /** The unique ID for the radio button. */
    _uniqueId: string = `ca-radio-${++nextUniqueId}`;

    @Input() id: string = this._uniqueId;

    /** Analog to HTML 'name' attribute used to group radios for unique selection. */
    @Input() name: string;

    /** Used to set the 'aria-label' attribute on the underlying input element. */
    @Input('aria-label') ariaLabel: string;

    /** The 'aria-labelledby' attribute takes precedence as the element's text alternative. */
    @Input('aria-labelledby') ariaLabelledby: string;

    /** The 'aria-describedby' attribute is read after the element's label and field type. */
    @Input('aria-describedby') ariaDescribedby: string;

    /**
     * Event emitted when the checked state of this radio button changes.
     * Change events are only emitted when the value changes due to user interaction with
     * the radio button (the same behavior as `<input type-"radio">`).
     */
    @Output() readonly change: EventEmitter<RadioChange> = new EventEmitter<RadioChange>();

    /** The parent radio group. May or may not be present. */
    radioGroup: RadioGroupDirective;

    /** The native `<input type=radio>` element */
    @ViewChild('input', { static: true }) _inputElement: ElementRef<HTMLInputElement>;
    tabIndex: number;
    private _labelPosition: 'before' | 'after';

    /** Whether this radio is checked. */
    private _checked: boolean = false;

    /** Whether this radio is disabled. */
    private _disabled: boolean;

    /** Whether this radio is required. */
    private _required: boolean;

    /** Value assigned to this radio. */
    private _value: any = null;
    constructor(
        @Optional() radioGroup: RadioGroupDirective,
        public _elementRef: ElementRef,
        private _changeDetector: ChangeDetectorRef,
        private _focusMonitor: FocusMonitor,
        private _radioDispatcher: UniqueSelectionDispatcher,
    ) {
        this.radioGroup = radioGroup;

        this._removeUniqueSelectionListener = _radioDispatcher.listen((id: string, name: string) => {
            if (id !== this.id && name === this.name) {
                this.checked = false;
            }
        });
    }

    /** Focuses the radio button. */
    focus(options?: FocusOptions): void {
        this._focusMonitor.focusVia(this._inputElement, 'keyboard', options);
    }

    /**
     * Marks the radio button as needing checking for change detection.
     * This method is exposed because the parent radio group will directly
     * update bound properties of the radio button.
     */
    _markForCheck(): void {
        // When group value changes, the button will not be notified. Use `markForCheck` to explicit
        // update radio button's status
        this._changeDetector.markForCheck();
    }

    ngOnInit(): void {
        if (this.radioGroup) {
            // If the radio is inside a radio group, determine if it should be checked
            this.checked = this.radioGroup.value === this._value;
            // Copy name from parent radio group
            this.name = this.radioGroup.name;
        }
    }

    ngAfterViewInit(): void {
        this._focusMonitor
            .monitor(this._elementRef as ElementRef<HTMLElement>, true)
            .pipe(
                tap(focusOrigin => {
                    if (!focusOrigin && this.radioGroup) {
                        this.radioGroup._touch();
                    }
                }),
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this._focusMonitor.stopMonitoring(this._elementRef as ElementRef<HTMLElement>);
        this._removeUniqueSelectionListener();
    }

    /** Dispatch change event with current value. */
    _onInputClick(event: Event): void {
        // We have to stop propagation for click events on the visual hidden input element.
        // By default, when a user clicks on a label element, a generated click event will be
        // dispatched on the associated input element. Since we are using a label element as our
        // root container, the click event on the `radio-button` will be executed twice.
        // The real click event will bubble up, and the generated click event also tries to bubble up.
        // This will lead to multiple click events.
        // Preventing bubbling for the second event will solve that issue.
        event.stopPropagation();
    }

    /**
     * Triggered when the radio button received a click or the input recognized any change.
     * Clicking on a label element, will trigger a change event on the associated input.
     */
    _onInputChange(event: Event): void {
        // We always have to stop propagation on the change event.
        // Otherwise the change event, from the input element, will bubble up and
        // emit its event object to the `change` output.
        event.stopPropagation();

        const groupValueChanged = this.radioGroup && this.value !== this.radioGroup.value;
        this.checked = true;
        this._emitChangeEvent();

        if (this.radioGroup) {
            this.radioGroup._controlValueAccessorChangeFn(this.value);
            if (groupValueChanged) {
                this.radioGroup._emitChangeEvent();
            }
        }
    }

    /** Unregister function for _radioDispatcher */
    private _removeUniqueSelectionListener: () => void = () => {
        // do nothing
    };
    private _emitChangeEvent(): void {
        this.change.emit(new RadioChange(this, this._value));
    }
}
